<script>
import isButtonFilled from '../../../utils/isButtonFilled';
import getQuestionColors from '../../../utils/getQuestionColors';
import getButtonColor from '../../../utils/getButtonColor';

export default {
  props: {
    questionData: {
      type: Object,
      required: true,
    },
    answer: {
      type: Number,
      default: null,
    },
    labelMin: {
      type: String,
      default: '',
    },
    labelMax: {
      type: String,
      default: '',
    },
    scales: {
      type: Array,
      required: true,
    },
  },
  computed: {
    isRounded() {
      const { button_design, scale_type } = this.questionData;
      return !(button_design === 1 || button_design === 2 || scale_type === 1);
    },
    isFilled() {
      const { button_design } = this.questionData;
      return isButtonFilled(button_design);
    },
    colorsArray() {
      return getQuestionColors(
        this.questionData.min_scale,
        this.questionData.max_scale,
        this.questionData.inverted_scale,
      );
    },
    hasLabel() {
      return this.labelMin || this.labelMax;
    },
  },
  methods: {
    handleAnswerChange(value) {
      this.$emit('change', value);
    },
    getColor(index) {
      return getButtonColor(
        index,
        this.questionData.color_pattern,
        this.colorsArray,
        this.questionData.custom_color,
      );
    },
  },
};
</script>

<template>
  <div v-if="!questionData.scale_type">
    <div class="cxm-ScaleMetrics-NumericButtonsContainer">
      <div
        ref="buttons"
        class="cxm-ScaleMetrics"
      >
        <div
          v-for="(scaleItem, index) in scales"
          :key="index"
        >
          <div
            :ref="'ref_' + index"
            class="cxm-ScaleMetrics-Button"
            :class="{
              '-selected': answer === scaleItem,
              '-filled': isFilled,
              '-rounded': isRounded,
              '-big': hasLabel
            }"
            :style="`--custom-btn-color:${getColor(index)}`"
            @click="() => handleAnswerChange(scaleItem)"
          >
            {{ scaleItem }}
          </div>
        </div>
      </div>
      <div class="cxm-ScaleMetrics-LabelContainer">
        <div class="cxm-ScaleMetrics-Label">
          {{ labelMin }}
        </div>
        <div class="cxm-ScaleMetrics-Label">
          {{ labelMax }}
        </div>
      </div>
    </div>
  </div>
  <div
    v-else
    class="cxm-ScaleMetrics"
  >
    <div
      v-for="(scaleItem, index) in scales"
      :ref="'ref_' + index"
      :key="index"
      :class="{
        'cxm-ScaleMetrics-Button': true,
        '-likert': true,
        '-selected': answer === scaleItem.value,
        scale7: questionData.min_scale === 7,
        '-filled': isFilled,
        '-rounded': isRounded
      }"
      :style="`--custom-btn-color:${getColor(index)}`"
      @click="() => handleAnswerChange(scaleItem.value)"
    >
      {{ scaleItem.label }}
    </div>
  </div>
</template>

<style scoped lang="scss">
.cxm-ScaleMetrics-NumericButtonsContainer {
  display: inline-flex;
  flex-direction: column;
}
.cxm-ScaleMetrics {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--track-spacing-xs);
}
.cxm-ScaleMetrics-Button {
  display: flex;
  width: 48px;
  height: 48px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: 1px solid;
  border-radius: var(--track-border-radius-sm);
  color: var(--custom-btn-color);
  font-family: var(--track-font-family-base);
  font-size: var(--track-font-size-xs);
  font-weight: var(--track-font-weight-semibold);
  &:hover,
  &.-selected {
    background-color: var(--custom-btn-color);
    color: white;
  }
  &.-filled {
    background-color: var(--custom-btn-color);
    color: white;
    &:hover,
    &.-selected {
      background-color: white;
      color: var(--custom-btn-color);
    }
  }
  &.-rounded {
    border-radius: 100%;
  }
  &.-selected {
    -webkit-box-shadow: 0px 4px 3px #ccc;
    -moz-box-shadow: 0px 4px 3px #ccc;
    box-shadow: 0px 4px 3px #ccc;
  }
  &.-likert {
    display: flex;
    width: 100px;
    height: 48px;
    padding: 0px 8px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    font-size: var(--track-font-size-xxs);
    font-weight: var(--track-font-weight-medium);
    line-height: var(--track-line-height-far);
  }
  &.-big {
    height: 48px;
    width: 48px;
  }
}
.cxm-ScaleMetrics-LabelContainer {
  display: flex;
  justify-content: space-between;
  margin-right: -88px;
  margin-left: -85px;
}
.cxm-ScaleMetrics-Label {
  color: var(--track-color-bluegray-80);
  text-align: right;
  font-family: var(--track-font-family-base);
  font-size: var(--track-font-size-xxs);
  font-weight: var(--track-font-weight-medium);
  line-height: var(--track-line-height-far);
}
</style>
